import { Avatar } from "@/components/buttons/Avatar";
import { useColorMode } from "@/components/layouts/ColorModeContext";
import { getSettings, getTheme } from "@/features/branding";
import { isSspLabelsToggleEnabled, readSspLabelsChoice, storeSspLabelsChoice } from "@/features/branding/sspLabels";
import supabase from "@/features/supabase";
import { User } from "@/store/services/supabase";
import { useKeys } from "@/utils/hooks/useKeys";
import ControlOutlined from "@ant-design/icons/es/icons/ControlOutlined";
import SettingOutlined from "@ant-design/icons/es/icons/SettingOutlined";
import { MoonIcon, SunIcon } from "@phosphor-icons/react";
import { Button, Dropdown, Space } from "antd";
import { ItemType } from "antd/lib/menu/interface";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
type AccountDropdownButtonProps = User;
export const AccountDropdownButton = (user?: AccountDropdownButtonProps) => {
  const {
    t
  } = useTranslation("authentication");
  const router = useRouter();
  const [keys] = useKeys();
  const {
    colorMode,
    toggleColorMode
  } = useColorMode();
  if (!user) {
    return <div>Not logged in</div>;
  }
  const buildSettingsItems = (): ItemType[] => {
    // Do not show settings button if restricted.
    // TODO: Remove this when settings can be enabled for everyone (we have email invites, etc.).
    if (!getSettings().enabled) {
      return [];
    }
    const settingsItems: ItemType[] = [];
    if (user.is_admin) {
      settingsItems.push({
        key: "admin",
        label: <Space align="center">
            <ControlOutlined />
            Admin Panel
          </Space>,
        onClick: () => router.push("/admin")
      });
    }
    settingsItems.push({
      key: "settings",
      label: <Space>
            <SettingOutlined />
            Settings
          </Space>,
      onClick: () => router.push("/settings/account/preferences")
    }, {
      type: "divider"
    });
    return settingsItems;
  };
  const buildColorModeItems = (): ItemType[] => {
    if (process.env.NEXT_PUBLIC_ENABLE_DARK_MODE !== "true") {
      return [];
    }
    const goingDark = colorMode === "light";
    return [{
      key: "colour-mode",
      label: <Space>
            {goingDark ? <MoonIcon size={16} /> : <SunIcon size={16} />}
            {goingDark ? "Dark mode" : "Light mode"}
          </Space>,
      onClick: toggleColorMode
    }, {
      type: "divider"
    }];
  };
  const buildScenarioLabelItems = (): ItemType[] => {
    if (!isSspLabelsToggleEnabled(getTheme())) {
      return [];
    }
    const sspLabels = readSspLabelsChoice();
    return [{
      key: "scenario-labels",
      label: sspLabels ? "Show RCP labels" : "Show SSP labels",
      onClick: () => {
        storeSspLabelsChoice(!sspLabels);
        // The labels load once at start-up.
        window.location.reload();
      }
    }, {
      type: "divider"
    }];
  };
  const items: ItemType[] = [{
    key: "email",
    label: <div>
          <div>{user.email}</div>
          <div className="my-0.5 text-sm leading-normal text-gray-500">
            <div>{user.admin_role_name || user.group_name}</div>
            <div>{user.org_name}</div>
          </div>
        </div>,
    style: {
      pointerEvents: "none"
    }
  }, {
    type: "divider"
  }, ...buildSettingsItems(), ...buildColorModeItems(), ...buildScenarioLabelItems(), {
    key: "logout",
    onClick: async () => {
      await supabase.auth.signOut();
      keys.reset();
      await router.replace("/login");
    },
    label: t("logOut")
  }];
  return <>
      <Dropdown menu={{
      items
    }} trigger={["click"]} className="cursor-pointer" data-sentry-element="Dropdown" data-sentry-source-file="AccountDropdownButton.tsx">
        <Button icon={<Avatar name={user.email || ""} size={32} />} shape="circle" type="text" data-sentry-element="Button" data-sentry-source-file="AccountDropdownButton.tsx"></Button>
      </Dropdown>
    </>;
};